<template>
  <div class="mt-4">
    <v-card class="mx-auto" max-width="95%" elevation="2">
      <v-card-text class="pa-4">
        <v-alert dense text type="info" class="mb-4 body-2">
          이 페이지는 <b>로컬 환경 전용</b> 도구입니다. S3의 메인 JSON을 직접 읽고 저장합니다.
        </v-alert>

        <!-- ===== 설정 탭 (carrygold 패턴) — 탭 바 오른쪽 끝에 마스터 JSON 링크 버튼 ===== -->
        <v-card outlined class="mb-3">
          <div class="d-flex align-center flex-wrap pr-2" style="border-bottom:1px solid #e0e0e0;">
            <!-- PC: 탭 / 모바일: 드롭다운 -->
            <v-tabs v-if="!isMobileTab" v-model="activeTab" color="primary" class="mt-0 flex-grow-1" show-arrows>
              <v-tab v-for="t in tabs" :key="t.key"><span class="font-weight-bold">{{ t.label }}</span></v-tab>
            </v-tabs>
            <v-select
              v-else
              v-model="activeTab"
              :items="tabItems"
              item-text="text"
              item-value="value"
              dense
              outlined
              hide-details
              color="primary"
              class="tab-select flex-grow-1 my-2 ml-2"
              :menu-props="{ offsetY: true, maxHeight: 400 }"
            >
              <template v-slot:selection="{ item }">
                <v-icon size="18" color="primary" class="mr-2">{{ item.icon }}</v-icon>
                <span class="font-weight-bold">{{ item.text }}</span>
              </template>
              <template v-slot:item="{ item, on, attrs }">
                <v-list-item v-bind="attrs" v-on="on">
                  <v-list-item-icon class="mr-3">
                    <v-icon size="20" color="primary">{{ item.icon }}</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{ item.text }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </template>
            </v-select>
            <v-spacer></v-spacer>
            <v-chip small outlined class="mr-2">{{ envLabel }}</v-chip>
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn v-bind="attrs" v-on="on" :href="s3Url" target="_blank" rel="noopener noreferrer"
                       color="indigo accent-3" dark rounded x-small class="mr-1">
                  <v-icon left small>mdi-open-in-new</v-icon>마스터 JSON
                </v-btn>
              </template>
              <span>{{ s3Key }} — 새 탭에서 열기</span>
            </v-tooltip>
            <v-btn icon x-small class="mr-1" @click="OnClick_CopyUrl(s3Url)" title="URL 복사">
              <v-icon small>mdi-content-copy</v-icon>
            </v-btn>
          </div>
          <v-tabs-items v-model="activeTab" :touchless="isMobileTab">
            <v-tab-item v-for="t in tabs" :key="t.key">
              <v-list class="pa-0">
                <div v-for="(section, sectionIdx) in fieldConfig.filter(s => s.tab === t.key)" :key="`section-${t.key}-${sectionIdx}`">
                  <v-list-item class="section-header" style="background:#E3F2FD; min-height: 48px;">
                    <v-list-item-content>
                      <v-list-item-title class="text-h6 font-weight-bold">{{ section.title }}</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-divider/>

                  <!-- 필드 그룹 -->
                  <div v-for="(group, groupIdx) in section.groups" :key="`group-${sectionIdx}-${groupIdx}`">
                    <v-list-item class="px-4 py-1" style="min-height: auto;">
                      <v-row class="align-center" no-gutters>
                        <v-col v-if="group.title" cols="12" md="2" class="pa-1 d-flex align-center" style="min-width: 80px; flex-shrink: 0;">
                          <div class="text-subtitle-2 font-weight-medium">{{ group.title }}</div>
                        </v-col>
                        <v-col
                          v-for="(field, fieldIdx) in group.fields"
                          :key="`field-${sectionIdx}-${groupIdx}-${fieldIdx}`"
                          :cols="12"
                          :md="group.title ? (field.cols || 10) : (field.cols || 12)"
                          class="pa-1"
                        >
                          <v-text-field
                            v-if="field.type === 'text'"
                            :label="field.label"
                            :placeholder="field.placeholder || ''"
                            v-model="fieldData[field.jsonKey]"
                            outlined dense hide-details
                            :class="{'mb-0': true, 'check-version-field': field.isCheckVersion}"
                            :style="field.isCheckVersion ? 'background-color: #FFF3E0;' : ''"
                          />
                          <v-select
                            v-else-if="field.type === 'select'"
                            :label="field.label"
                            v-model="fieldData[field.jsonKey]"
                            :items="getOptions(field.options)"
                            item-text="name" item-value="value"
                            outlined dense hide-details
                          />
                          <v-textarea
                            v-else-if="field.type === 'textarea'"
                            :label="field.label"
                            :placeholder="field.placeholder || ''"
                            v-model="fieldData[field.jsonKey]"
                            outlined dense hide-details
                            :rows="field.rows || 3"
                          />
                        </v-col>
                      </v-row>
                    </v-list-item>
                    <v-divider v-if="groupIdx < section.groups.length - 1" class="mx-4"/>
                  </div>

                  <!-- 점검 메시지 필드 (서버 점검 상태 섹션에서만, 점검중일 때 노출) -->
                  <v-list-item v-if="section.title.includes('서버 점검 상태') && IsServerChecking()" class="px-4 py-1" style="min-height: auto;">
                    <v-col cols="12" class="pa-1">
                      <v-text-field
                        label="점검 메시지"
                        placeholder="점검 시간 동안 노출할 메시지를 입력해주세요."
                        v-model="fieldData.SERVER_CHECKING_MSG"
                        outlined dense hide-details
                      />
                    </v-col>
                  </v-list-item>

                  <v-divider class="my-1"/>
                </div>
              </v-list>
            </v-tab-item>
          </v-tabs-items>
        </v-card>

        <v-card outlined>
          <v-card-text class="pa-3 text-center">
            <v-btn color="indigo accent-3" dark width="180px" @click="OnClick_Save" :loading="saving">
              <v-icon left>mdi-content-save</v-icon>전체 저장
            </v-btn>
          </v-card-text>
        </v-card>
      </v-card-text>
    </v-card>
    <div class="py-12"></div>
  </div>
</template>

<script>
import config from '../../config/config';
const { FileUpload_JSON } = require('../../util/fileutil');

export default {
  name: 'Prombook_Setting',
  data: () => ({
    loading: false,
    saving: false,
    activeTab: 0,
    tabs: [
      { key: 'general', label: '일반설정', icon: 'mdi-cog' },
      { key: 'event',   label: '이벤트',   icon: 'mdi-star' },
    ],

    // 자동 폼 데이터 (fieldConfig 기반)
    fieldData: {},

    rawMasterDoc: {},
    promptList: [],
    show_raw_json: false,

    // === 옵션 리스트 ===
    serverStateOptions: [
      { name: '1 · 정상 운영',  value: '1' },
      { name: '2 · 점검 중',    value: '2' },
      { name: '3 · 긴급 점검',  value: '3' },
      { name: '4 · 서비스 종료', value: '4' },
    ],

    // === 필드 설정 (carrygold 패턴) ===
    fieldConfig: [
      // ──────── [서버 관리] ────────
      {
        tab: 'general',
        title: '[서버 관리]',
        groups: [
          {
            fields: [
              { jsonKey: 'NAME', label: '서비스 이름', type: 'text', default: '프롬북', cols: 12 },
            ],
          },
          {
            title: '최신버전',
            fields: [
              { jsonKey: 'VERSION_AOS', label: 'AOS', type: 'text', placeholder: 'APK version', default: '', cols: 5 },
              { jsonKey: 'VERSION_IOS', label: 'IOS', type: 'text', placeholder: 'IPA version', default: '', cols: 5 },
            ],
          },
          {
            title: '최소(강제업뎃)',
            fields: [
              { jsonKey: 'VERSION_AOS_MIN', label: 'AOS', type: 'text', placeholder: 'APK version', default: '', cols: 5 },
              { jsonKey: 'VERSION_IOS_MIN', label: 'IOS', type: 'text', placeholder: 'IPA version', default: '', cols: 5 },
            ],
          },
          {
            title: '검수버전',
            fields: [
              { jsonKey: 'CHECK_VERSION_AOS', label: 'AOS', type: 'text', placeholder: '검수중이 없으면 비워두세요.', default: '', cols: 5, isCheckVersion: true },
              { jsonKey: 'CHECK_VERSION_IOS', label: 'IOS', type: 'text', placeholder: '검수중이 없으면 비워두세요.', default: '', cols: 5, isCheckVersion: true },
            ],
          },
        ],
      },
      // ──────── [ 쿠폰교환소 ] ────────
      {
        tab: 'general',
        title: '[ 쿠폰교환소 ]',
        groups: [
          {
            fields: [
              { jsonKey: 'PNJ_COUPON_SHOP_TYPE', label: '(PNJ)쿠폰교환소', type: 'text', placeholder: '예: main_shop', default: '', cols: 6 },
              { jsonKey: 'PNJ_COUPON_SHOP_TYPE_REVIEW', label: '(PNJ)쿠폰교환소 검수전용', type: 'text', default: '', cols: 6 },
            ],
          },
        ],
      },
      // ──────── [서버 점검 상태] ────────
      {
        tab: 'general',
        title: '[ 서버 점검 상태 ]',
        groups: [
          {
            fields: [
              { jsonKey: 'SERVER_STATE_AOS', label: 'AOS 서버 상태', type: 'select', options: 'serverStateOptions', default: '1', cols: 6 },
              { jsonKey: 'SERVER_STATE_IOS', label: 'IOS 서버 상태', type: 'select', options: 'serverStateOptions', default: '1', cols: 6 },
            ],
          },
        ],
      },
      // ──────── [ 설정 ] ────────
      {
        tab: 'general',
        title: '[ 설정 ]',
        groups: [
          {
            fields: [
              { jsonKey: 'PLACEKEY_RECOMMEND', label: '당신을 위한 추천미션', type: 'text', placeholder: '예: prombook_recommend', default: '', cols: 12 },
            ],
          },
          {
            fields: [
              { jsonKey: 'PLACEKEY_POINT_TAB_LIST', label: '포인트탭 나열 미션', type: 'text', placeholder: '예: prombook_point_tab_list', default: '', cols: 12 },
            ],
          },
          {
            fields: [
              { jsonKey: 'PHONE_VERIFY_EMAIL', label: '전화번호 인증 이메일', type: 'text', placeholder: '예: verify@pnjstudio.com', default: '', cols: 12 },
            ],
          },
          {
            fields: [
              { jsonKey: 'INQUIRY_EMAIL', label: '문의사항 이메일', type: 'text', placeholder: '예: help@pnjstudio.com', default: '', cols: 12 },
            ],
          },
        ],
      },
      // ──────── [ S3 / 네트워크 ] ────────
      {
        tab: 'general',
        title: '[ S3 / 네트워크 ]',
        groups: [
          {
            fields: [
              { jsonKey: 'S3_BASE_PATH', label: 'S3 BasePath', type: 'text', placeholder: '예: https://content-filecenter.s3.ap-northeast-2.amazonaws.com/', default: '', cols: 12 },
            ],
          },
          {
            fields: [
              { jsonKey: 'S3_CLOUD_PATH', label: 'S3 CloudPath', type: 'text', placeholder: '예: https://cdn.pnjstudio.com/', default: '', cols: 12 },
            ],
          },
          {
            fields: [
              { jsonKey: 'WEBSOCKET_URL', label: '웹소켓 주소', type: 'text', placeholder: '예: wss://ws.pnjstudio.com', default: '', cols: 12 },
            ],
          },
        ],
      },
      // ──────── [ 이벤트 설정 ] (event 탭) ────────
      {
        tab: 'event',
        title: '[ 이벤트 설정 ]',
        groups: [
          {
            fields: [
              { jsonKey: 'COUPANG_LINK', label: '쿠팡 링크', type: 'text', placeholder: '쿠팡 링크 URL을 입력하세요', default: '', cols: 12 },
            ],
          },
          {
            fields: [
              { jsonKey: 'REVIEW_EVENT_PATH', label: '리뷰 이벤트 경로', type: 'text', placeholder: '리뷰 이벤트 경로를 입력하세요', default: '', cols: 12 },
            ],
          },
          {
            fields: [
              { jsonKey: 'ROULETTE_EVENT_CREATE', label: '룰렛 이벤트 생성', type: 'text', placeholder: '룰렛 이벤트 생성 값을 입력하세요', default: '', cols: 12 },
            ],
          },
        ],
      },
    ],
  }),
  computed: {
    // 모바일/태블릿에서는 탭 헤더 대신 드롭다운을 노출한다.
    // (가로 스크롤 중 스와이프가 탭 전환으로 잡히는 문제 방지)
    isMobileTab() { return this.$vuetify.breakpoint.smAndDown; },
    // tabs 를 v-select 용(인덱스 기반)으로 변환 — activeTab 은 탭 인덱스이므로 value 도 인덱스.
    tabItems() {
      return this.tabs.map((t, i) => ({ value: i, text: t.label, icon: t.icon }));
    },
    envLabel() { return config.isLive ? 'LIVE' : (config.isLocal ? 'LOCAL' : 'DEV'); },
    s3Url() { return config.promptMaster_URL; },
    s3Key() { return config.promptMaster_S3_KEY; },
    listUrl() { return config.promptList_URL; },
    stats() {
      const total = this.promptList.length;
      const active = this.promptList.filter(p => p.is_active === 'Y').length;
      return { total, active, inactive: total - active };
    },
    rawJsonPreview() {
      try { return JSON.stringify(this.rawMasterDoc || {}, null, 2); }
      catch (e) { return String(e); }
    },
  },
  created() {
    this.initializeFieldData();
    this.LoadMaster();
  },
  methods: {
    // 점검 메시지 입력란 노출 조건 (carrygold 패턴 — 둘 중 하나라도 '점검/긴급')
    IsServerChecking() {
      const a = this.fieldData.SERVER_STATE_AOS;
      const b = this.fieldData.SERVER_STATE_IOS;
      return a === '2' || a === '3' || b === '2' || b === '3';
    },
    getOptions(key) { return this[key] || []; },

    // fieldConfig 기반 fieldData 초기화 (기본값 세팅)
    initializeFieldData() {
      this.fieldConfig.forEach(section => {
        section.groups.forEach(group => {
          group.fields.forEach(field => {
            const def = Array.isArray(field.default)
              ? JSON.parse(JSON.stringify(field.default))
              : field.default;
            this.$set(this.fieldData, field.jsonKey, def);
          });
        });
      });
      // 특수 필드 — 점검 메시지 기본값
      if (this.fieldData.SERVER_CHECKING_MSG === undefined) {
        this.$set(this.fieldData, 'SERVER_CHECKING_MSG', '죄송합니다. 지금은 잠시 서버 점검 중입니다.');
      }
    },

    // JSON 데이터를 fieldData 로 자동 로드
    loadFieldsFromJson(jsonData) {
      this.fieldConfig.forEach(section => {
        section.groups.forEach(group => {
          group.fields.forEach(field => {
            const v = jsonData[field.jsonKey];
            if (v !== undefined) {
              const next = Array.isArray(v) ? JSON.parse(JSON.stringify(v)) : v;
              this.$set(this.fieldData, field.jsonKey, next);
            }
          });
        });
      });
      // 특수 필드
      if (jsonData.SERVER_CHECKING_MSG !== undefined) {
        this.$set(this.fieldData, 'SERVER_CHECKING_MSG', jsonData.SERVER_CHECKING_MSG);
      }
    },

    async LoadMaster() {
      this.loading = true;
      // 마스터 (서버 설정 전용)
      try {
        const ts = Date.now();
        const res = await fetch(`${this.s3Url}?t=${ts}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        // 호환성: 옛 마스터에 PROMPT_LIST 가 섞여 있어도 분리해서 표시
        const { PROMPT_LIST: _legacyList, ...settingsOnly } = data || {};
        this.loadFieldsFromJson(settingsOnly);
        this.rawMasterDoc = settingsOnly;
      } catch (err) {
        console.warn('[PromptMaster] LoadMaster (settings) 실패:', err);
        this.initializeFieldData();
        this.rawMasterDoc = {};
      }
      // 통계용 — 프롬프트 리스트 (별도 파일, best-effort)
      try {
        const ts = Date.now();
        const res = await fetch(`${this.listUrl}?t=${ts}`);
        if (res.ok) {
          const d = await res.json();
          this.promptList = Array.isArray(d.PROMPT_LIST) ? d.PROMPT_LIST : [];
        } else {
          this.promptList = [];
        }
      } catch (err) {
        console.warn('[PromptMaster] LoadList (count only) 실패:', err);
        this.promptList = [];
      }
      this.loading = false;
    },

    OnClick_Refresh() { this.LoadMaster(); },

    async OnClick_CopyUrl(url) {
      const target = url || this.s3Url;
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(target);
        } else {
          const ta = document.createElement('textarea');
          ta.value = target; document.body.appendChild(ta); ta.select();
          document.execCommand('copy'); document.body.removeChild(ta);
        }
        alert('URL 을 클립보드에 복사했습니다.');
      } catch (e) {
        console.error(e); alert('복사에 실패했습니다.');
      }
    },

    async OnClick_CopyJson() {
      const text = this.rawJsonPreview;
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text);
        } else {
          const ta = document.createElement('textarea');
          ta.value = text; document.body.appendChild(ta); ta.select();
          document.execCommand('copy'); document.body.removeChild(ta);
        }
        alert('JSON 을 클립보드에 복사했습니다.');
      } catch (e) {
        console.error(e); alert('복사에 실패했습니다.');
      }
    },

    async OnClick_Save() {
      if (!confirm('S3의 PromptMaster.json을 덮어쓰시겠습니까?')) return;
      this.saving = true;
      try {
        // 마스터 JSON 은 서버 설정만 저장. PROMPT_LIST 는 별도 파일에서 관리.
        const nowIso = new Date().toISOString();
        const payload = {
          ...this.rawMasterDoc,        // 옛 키 유지 (모르는 키 보존)
          ...this.fieldData,           // 자동 폼 필드
          UPDATED_AT: nowIso,
        };
        delete payload.PROMPT_LIST;    // 마스터에는 절대 포함하지 않음

        const ok = await FileUpload_JSON(payload, this.s3Key);
        if (!ok) throw new Error('업로드 실패');
        this.rawMasterDoc = payload;
        alert('저장되었습니다.');
      } catch (err) {
        console.error(err);
        alert('저장에 실패했습니다: ' + (err.message || err));
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.raw-json {
  background: #f5f5f5;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 12px;
  font-size: 11px;
  line-height: 1.4;
  max-height: 480px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-all;
}
.section-header { border-radius: 0; }
.tab-select {
  max-width: 100%;
  background-color: #fff;
}
.check-version-field ::v-deep .v-input__slot {
  background-color: #FFF3E0 !important;
}
</style>
